import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import Title from "src/Shared/Components/Title/Title";
import { useStyles } from "./CompanyContactGrid.styles";
import { CustomDataGrid } from "src/Shared/Components";
import { contactColumns } from "./CompanyContactGrid.column";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { useEffect, useState } from "react";
import { IPageRequest } from "src/Shared/Interfaces";
import { GridPaginationModel } from "@mui/x-data-grid";
import { IContactFilter } from "../../Interfaces/IContactFilter";
import { ICompanyContact } from "../../Interfaces/ICompanyContact";
import { useParams } from "react-router";
import { IRouteParams } from "../../Interfaces";
import AddOrEditContact from "../AddOrEditContact/AddOrEditContact";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import { useModal } from "src/Shared/Hooks";
import { observer } from "mobx-react";
import ConditionalWrapper from "src/Shared/Components/ConditionalWrapper/ConditionalWrapper";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import { ToastService } from "src/Shared/Services";
import CompanyContactService from "../../Services/CompanyContact.service";
import CompanyOfficeService from "../../Services/CompanyOffice.service";
import { ICompanyOffice } from "../../Interfaces";
import { TESTING_OCCASIONS } from "../../Enums/TestingOccasion.enum";

/** The seven occasions a notification can be narrowed to (S4.3). */

type Props = {
  subMenuRender?: boolean;
  /** A submitted onboarding disables adding and editing in the wizard (decision 6). */
  readOnly?: boolean;
  /** Wizard step 4 requires an Imperans role before a contact saves; the record does not (S4.1). */
  requireImperansRole?: boolean;
};

const CompanyContactGrid: React.FC<Props> = ({
  subMenuRender,
  readOnly,
  requireImperansRole,
}) => {
  const {
    rootStore: {
      companyContactStore: {
        gridLoading,
        companyContactList,
        getCompanyContact,
        refreshCompanyContactList,
        primaryContactID,
        healthTickPrimaryContactID,
      },
      commonStore: { vendorTypes },
      companyStore: { companyOverview, getCompanyMenu },
    },
  } = useStore();
  const classes = useStyles();
  const { companyRowID } = useParams<Partial<IRouteParams>>();

  const [gridPagination, setGridPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sites, setSites] = useState<ICompanyOffice[]>([]);
  // True for as long as a Main/RTP change is in flight. The row's own checked flag only flips once
  // the refetch below lands, so without this a fast double click (or a click on a different row)
  // could fire a second, racing change while the first was still saving.
  const [savingSingleton, setSavingSingleton] = useState<boolean>(false);
  const [pendingRemoval, setPendingRemoval] = useState<ICompanyContact | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const onGridPaginationChange = (data: GridPaginationModel) => {
    setGridPagination(data);
    setRequest({
      filterCollection: {
        companyRowID: companyRowID,
      },
      sortCollection: [],
      pageNumber: data.page + 1,
      pageSize: data.pageSize,
    });
  };

  const modal = useModal();

  const updateContact = (companyContact: ICompanyContact) => {
    if (readOnly) return;
    modal.open(
      <AddOrEditContact
        mode={VIEW_MODE.EDIT}
        companyContact={companyContact}
        companyRowID={companyRowID}
        primayContactId={primaryContactID}
        healthTickPrimaryContactID={healthTickPrimaryContactID}
        requireImperansRole={requireImperansRole}
        countryCode={companyOverview?.countryCode}
      />,
    );
  };

  const addNewContact = () => {
    modal.open(
      <AddOrEditContact
        mode={VIEW_MODE.ADD}
        companyRowID={companyRowID}
        primayContactId={primaryContactID}
        healthTickPrimaryContactID={healthTickPrimaryContactID}
        requireImperansRole={requireImperansRole}
        countryCode={companyOverview?.countryCode}
      />,
    );
  };

  const [request, setRequest] = useState<IPageRequest<IContactFilter>>({
    pageNumber: 1,
    pageSize: 10,
    filterCollection: {
      companyRowID: companyRowID,
    },
    sortCollection: [],
  });

  useEffect(() => {
    getCompanyContact(request);
  }, [request]);

  // The site list behind the "Site access" column (S4.1).
  useEffect(() => {
    CompanyOfficeService.getCompanyOfficesList({
      pageNumber: 1,
      pageSize: 200,
      filterCollection: { companyRowID },
      sortCollection: [],
    }).subscribe({
      next: (data) => setSites(data?.results || []),
      error: () => setSites([]),
    });
  }, [companyRowID]);

  /**
   * The Main and RTP radios move the singleton; the server clears the previous holder (S4.2).
   *
   * Guarded by savingSingleton rather than firing straight through: the radios stay clickable until
   * the refetch below actually lands, so without this guard a fast double click could send the same
   * change twice, or send a second, different change while the first was still in flight.
   */
  const setSingletonFlag = (
    contact: ICompanyContact,
    changes: Partial<ICompanyContact>,
  ) => {
    if (savingSingleton) return;
    setSavingSingleton(true);
    CompanyContactService.updateompanyContact({ ...contact, ...changes }).subscribe({
      next: () => {
        refreshCompanyContactList(companyRowID);
        setSavingSingleton(false);
      },
      error: (error: AxiosError) => {
        ToastService.axiosError(error);
        setSavingSingleton(false);
      },
    });
  };

  /**
   * Removing a contact is the only route to test 5605's "removing the main contact clears it to
   * none rather than promoting silently" and 5607's "no contacts" path once the client already has
   * one — neither was reachable while contacts could be added but never removed (bug 5829).
   */
  const removeContact = (contact: ICompanyContact) => setPendingRemoval(contact);

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    setRemoving(true);
    CompanyContactService.deleteContact(pendingRemoval.companyContactID)
      .pipe(finalize(() => setRemoving(false)))
      .subscribe({
        next: (data) => {
          ToastService.success(data.message);
          setPendingRemoval(null);
          refreshCompanyContactList(companyRowID);
          getCompanyMenu(companyRowID, false);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <>
      <ConditionalWrapper
        condition={!subMenuRender}
        wrapper={(children) => (
          <MainContentBox>
            <Title title="Client Contacts"></Title>
            {children}
          </MainContentBox>
        )}
      >
        <Box className={subMenuRender ? classes.root_1 : classes.root}>
           {subMenuRender && (
            <Typography className={classes.title} variant="h5">
Client Contacts            </Typography>
          )}
          <Button variant="contained" disabled={readOnly} onClick={addNewContact}>
            Add Contacts
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <CustomDataGrid
            rowHeight={64}
            loading={gridLoading}
            rows={companyContactList.results || []}
            columns={contactColumns(
              updateContact,
              vendorTypes,
              sites,
              (contact) => setSingletonFlag(contact, { isPrimaryContact: true }),
              // Making a contact RTP also grants results access server-side if they lack it.
              (contact) => setSingletonFlag(contact, { isRequestingTestPersonnel: true }),
              TESTING_OCCASIONS,
              readOnly,
              savingSingleton,
              removeContact,
            )}
            rowCount={companyContactList.totalNumberOfRecords || 0}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body1">
                    No contacts yet — add the main contact to get started.
                  </Typography>
                </Box>
              ),
            }}
            getRowId={(row: ICompanyContact) => row.companyContactID}
            style={{
              height: "calc(100vh - 245px)",
            }}
            paginationModel={gridPagination}
            onPaginationModelChange={(model, _) =>
              onGridPaginationChange(model)
            }
            onRowDoubleClick={(params) => updateContact(params.row)}
          />
        </Box>
      </ConditionalWrapper>

      {/* Removing the main or RTP contact clears that flag to none rather than promoting another
          contact silently — the confirmation says so up front rather than after the fact. */}
      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Remove {pendingRemoval?.fullName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Removing “{pendingRemoval?.fullName}” clears its site access and Imperans access. If this
            contact is currently Main or RTP, that role is cleared to none rather than moved to
            another contact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={removing} onClick={confirmRemoval}>
            Remove contact
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(CompanyContactGrid);
